import { useId, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Play } from "lucide-react";
import { Badge, type BadgeTone } from "../../components/ui/badge";
import { useHasPermission } from "../../context/AuthContext";
import { cn } from "../../lib/utils";
import { fetchCurrentVersion, saveCandidateAsVersion } from "./agent-version";
import { callAnalyticsApi, errorText } from "./api";
import { AgentSelect } from "./agent-select";
import { ConfirmDialog } from "./confirm-dialog";
import { DASH, formatDateTime, formatRate, lineDiff } from "./format";
import type { ImprovementRun, ImprovementStatus } from "./types";

const STATUS_TONE: Record<ImprovementStatus, BadgeTone> = {
  queued: "neutral",
  running: "brand",
  completed: "success",
  failed: "danger",
};

const inFlight = (run: ImprovementRun | undefined) => run?.status === "queued" || run?.status === "running";

function DiffView({ before, after }: { before: string; after: string }) {
  const lines = lineDiff(before, after);
  return (
    <pre className="overflow-x-auto rounded-lg border border-line bg-canvas p-2 text-xs leading-5" aria-label="Prompt change">
      {lines.map((line, i) => (
        <div
          key={i}
          className={cn(
            "whitespace-pre-wrap break-words px-1",
            line.kind === "removed" && "bg-danger-soft text-danger",
            line.kind === "added" && "bg-success-soft text-success",
            line.kind === "same" && "text-ink-2",
          )}
        >
          <span aria-hidden className="mr-2 select-none">{line.kind === "removed" ? "−" : line.kind === "added" ? "+" : " "}</span>
          {line.kind === "removed" ? <span className="sr-only">Removed: </span> : line.kind === "added" ? <span className="sr-only">Added: </span> : null}
          {line.text || " "}
        </div>
      ))}
    </pre>
  );
}

function SaveAsVersion({ run }: { run: ImprovementRun }) {
  const nameId = useId();
  const canEditAgents = useHasPermission("agents.edit");
  const proposal = run.proposal!;
  const current = useQuery({
    queryKey: ["call-analytics", "agent-current-version", run.agent_id],
    queryFn: () => fetchCurrentVersion(run.agent_id),
    enabled: canEditAgents,
  });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(() => `Call analytics improvement ${new Date().toISOString().slice(0, 10)}`);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (!canEditAgents) return <p className="text-xs text-ink-3">Saving this as an agent version needs the Edit agents permission.</p>;
  if (current.isLoading) return <p className="text-xs text-ink-3" role="status">Checking the agent's current version…</p>;
  if (current.isError) return <p role="alert" className="text-xs text-danger">{errorText(current.error, "The agent's current version could not be read.")}</p>;
  if (current.data && current.data.version_id !== proposal.base_version_id) {
    return (
      <p role="alert" className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-sm text-warning">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        The agent has changed since this proposal was made (it was based on {proposal.base_version_id}, the agent is now on {current.data.version_id}). Start a new run instead of saving this one.
      </p>
    );
  }
  if (saved) return <p role="status" className="text-sm text-success">Saved as a new version of the agent.</p>;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveCandidateAsVersion({
        agentId: run.agent_id,
        baseVersionId: proposal.base_version_id,
        candidatePrompt: proposal.candidate_prompt,
        versionName: name.trim(),
      });
      setSaved(true);
      setOpen(false);
    } catch (e) {
      setError(errorText(e, "The version could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="rounded-lg bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover">
        Save as new agent version
      </button>
      {open ? (
        <ConfirmDialog title="Save as a new agent version?" confirmLabel="Save version" busy={saving || !name.trim()} onConfirm={save} onClose={() => setOpen(false)}>
          <p>The proposed prompt becomes the agent's current version and is used on its next calls. Everything else in the version is copied unchanged.</p>
          <label htmlFor={nameId} className="block text-xs font-medium text-ink-3">Version name</label>
          <input
            id={nameId}
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            className="h-9 w-full rounded-lg border border-line bg-canvas px-2 text-sm text-ink"
          />
          {error ? <p role="alert" className="text-danger">{error}</p> : null}
        </ConfirmDialog>
      ) : null}
    </>
  );
}

function RunView({ runId }: { runId: string }) {
  const query = useQuery({
    queryKey: ["call-analytics", "improvement", runId],
    queryFn: () => callAnalyticsApi.getImprovement(runId),
    refetchInterval: (q) => (inFlight(q.state.data) ? 10_000 : false),
  });
  const run = query.data;
  if (query.isLoading) return <div className="flex h-32 items-center justify-center" role="status" aria-label="Loading run"><Loader2 className="animate-spin text-brand" /></div>;
  if (query.isError || !run) return <p role="alert" className="text-sm text-danger">{errorText(query.error, "This run could not be loaded.")}</p>;

  const ev = run.evaluation;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-ink">Run from {formatDateTime(run.created_at)}</h3>
        <Badge tone={STATUS_TONE[run.status]}>{run.status}</Badge>
      </div>
      {inFlight(run) ? <p role="status" className="text-sm text-ink-3">This run is still working. The page checks again every few seconds.</p> : null}
      {run.error ? <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">{run.error}</p> : null}

      <section className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-3">Shortcomings found</h4>
        {run.shortcomings?.length ? (
          <ul className="divide-y divide-line rounded-xl border border-line">
            {run.shortcomings.map((s) => (
              <li key={s.key} className="space-y-1 px-3 py-2 text-sm">
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-medium text-ink">{s.label}</span>
                  <span className="text-xs text-ink-3">{s.count} calls · {formatRate(s.share)}</span>
                </div>
                {s.examples.slice(0, 2).map((example, i) => <p key={i} className="text-xs italic text-ink-2">{example}</p>)}
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-ink-3">{run.status === "completed" ? "None." : DASH}</p>}
      </section>

      {run.proposal ? (
        <section className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-3">Proposed changes (from version {run.proposal.base_version_id})</h4>
          {run.proposal.rationale ? <p className="text-sm text-ink-2">{run.proposal.rationale}</p> : null}
          {run.proposal.changes.map((change, i) => (
            <div key={i} className="space-y-1">
              <p className="text-sm text-ink">{change.reason}</p>
              <DiffView before={change.old} after={change.new} />
            </div>
          ))}
        </section>
      ) : null}

      {ev ? (
        <section className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-3">Evaluation</h4>
          <div className="flex flex-wrap items-center gap-2 text-sm text-ink-2">
            <Badge tone={ev.passed ? "success" : "danger"}>{ev.passed ? "Passed" : "Did not pass"}</Badge>
            <span>{ev.cases} replayed calls · pass rate {formatRate(ev.baseline_pass_rate)} → {formatRate(ev.candidate_pass_rate)}</span>
            <span>· {ev.fixed.length} fixed · {ev.regressions.length} regressed</span>
          </div>
          {ev.reason ? <p className="text-sm text-ink-2">{ev.reason}</p> : null}
          {ev.case_results.length ? (
            <div className="overflow-x-auto rounded-xl border border-line">
              <table className="w-full text-sm">
                <thead className="bg-soft text-left text-xs text-ink-3">
                  <tr>
                    <th scope="col" className="px-3 py-2 font-medium">Call</th>
                    <th scope="col" className="px-3 py-2 font-medium">Current prompt</th>
                    <th scope="col" className="px-3 py-2 font-medium">Proposed prompt</th>
                  </tr>
                </thead>
                <tbody>
                  {ev.case_results.map((c) => (
                    <tr key={c.call_id} className="border-t border-line align-top">
                      <td className="px-3 py-1.5 font-mono text-xs text-ink-2">{c.call_id}</td>
                      {[{ ok: c.baseline, why: c.baseline_reason }, { ok: c.candidate, why: c.candidate_reason }].map((cell, i) => (
                        <td key={i} className="px-3 py-1.5">
                          <Badge tone={cell.ok === true ? "success" : cell.ok === false ? "danger" : "neutral"}>{cell.ok === true ? "Pass" : cell.ok === false ? "Fail" : DASH}</Badge>
                          {cell.why ? <div className="mt-0.5 text-xs text-ink-3">{cell.why}</div> : null}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {ev.passed && run.proposal ? <SaveAsVersion run={run} /> : (
            <p className="text-xs text-ink-3">Only a proposal whose evaluation passed can be saved as an agent version.</p>
          )}
        </section>
      ) : null}
      <p className="text-xs text-ink-3">A run never changes the agent by itself.</p>
    </div>
  );
}

export function ImprovementsTab() {
  const canConfigure = useHasPermission("call_analytics.configure");
  const queryClient = useQueryClient();
  const [agentId, setAgentId] = useState("");
  const [runId, setRunId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const runs = useQuery({
    queryKey: ["call-analytics", "improvements", agentId],
    queryFn: () => callAnalyticsApi.listImprovements(agentId),
    enabled: Boolean(agentId),
  });

  const start = async () => {
    setStarting(true);
    setStartError(null);
    try {
      const run = await callAnalyticsApi.startImprovement(agentId);
      setConfirming(false);
      setRunId(run.run_id);
      await queryClient.invalidateQueries({ queryKey: ["call-analytics", "improvements", agentId] });
    } catch (e) {
      setStartError(errorText(e, "The run could not be started."));
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-3">
        A run reads this agent's analysed calls, proposes prompt changes for what keeps failing, and replays past calls to check them.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <AgentSelect value={agentId} onChange={(id) => { setAgentId(id); setRunId(null); }} />
        {canConfigure && agentId ? (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-brand px-3 text-sm font-semibold text-white hover:bg-brand-hover"
          >
            <Play className="h-3.5 w-3.5" aria-hidden /> Start a run
          </button>
        ) : null}
      </div>

      {agentId ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(16rem,20rem)_1fr]">
          <section aria-label="Improvement runs" className="overflow-hidden rounded-2xl border border-line bg-elevated">
            {runs.isLoading ? (
              <div className="flex h-24 items-center justify-center" role="status" aria-label="Loading runs"><Loader2 className="animate-spin text-brand" /></div>
            ) : runs.isError ? (
              <p role="alert" className="p-4 text-sm text-danger">{errorText(runs.error, "Runs could not be loaded.")}</p>
            ) : runs.data?.items.length ? (
              <ul className="divide-y divide-line">
                {runs.data.items.map((run) => (
                  <li key={run.run_id}>
                    <button
                      type="button"
                      onClick={() => setRunId(run.run_id)}
                      aria-current={run.run_id === runId ? "true" : undefined}
                      className={cn("flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm", run.run_id === runId ? "bg-brand/10" : "hover:bg-soft")}
                    >
                      <span className="text-ink">{formatDateTime(run.created_at)}</span>
                      <Badge tone={STATUS_TONE[run.status]}>{run.status}</Badge>
                    </button>
                  </li>
                ))}
              </ul>
            ) : <p className="p-4 text-sm text-ink-3">No runs for this agent yet.</p>}
          </section>
          <section aria-label="Improvement run" className="min-w-0 rounded-2xl border border-line bg-elevated p-4">
            {runId ? <RunView key={runId} runId={runId} /> : <p className="py-10 text-center text-sm text-ink-3">Select a run.</p>}
          </section>
        </div>
      ) : <p className="text-sm text-ink-3">Choose an agent to see its improvement runs.</p>}

      {confirming ? (
        <ConfirmDialog title="Start an improvement run?" confirmLabel="Start run" busy={starting} onConfirm={start} onClose={() => { setConfirming(false); setStartError(null); }}>
          <p>The run uses language-model time to analyse failures and replay calls. It does not change the agent.</p>
          {startError ? <p role="alert" className="text-danger">{startError}</p> : null}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
